import React, { useCallback, useEffect, useReducer, useState } from 'react';
import {
    Button,
    Form,
    FormGroup,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { onChangeFunction, UrlEnum } from '../../../shared';
import { FormSelect } from '../../blocks';
import { getCipLabel } from '../../academic-affair-report/program-criteria-form';
import { getOptionLabel } from '../../academic-affair-program/search-academic-programs-form';
import { useHistory } from 'react-router-dom';
import format from 'date-fns/format';
import { academicProgramsService, capitalProjectService } from '../../../services';
import queryString from 'query-string';
import { RadioCheckGroup } from '../../academic-affair-report/components/RadioCheckGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faFile,
    faPaperclip,
    faTimes,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import eventEmitter from "../../../shared/eventEmitter";

export const ProgramRequestEditSplitProgramRequestModal = ({
    isOpen,
    toggle,
    institutions,
    details,
    degreeLevels,
    INITIAL_STATE,
    setDetails,
}) => {
    const programStatusOptions = [
        { label: 'New', value: 0 },
        { label: 'Active', value: 1 },
        { label: 'Suspended', value: 3 },
        { label: 'Eliminated', value: 4 },
        { label: 'Merged', value: 5 },
    ];
    const locationStatusOptions = [
        { label: 'Active', value: 1 },
        { label: 'Suspended', value: 3 },
        { label: 'Eliminated', value: 4 },
    ];
    const history = useHistory();
    const [onlineDropdownDisabled, setOnlineDropdownDisabled] = useState(false);
    const [locationsToAdd, setLocationsToAdd] = useState([]);
    const [validationError, setValidationError] = useState('');
    const reducer = (state, newState) => ({ ...state, ...newState });

    const [userInput, setUserInput] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        INITIAL_STATE
    );
    const [files, setFiles] = useState(null);

    const [splitPrograms, setSplitPrograms] = useState([
        {
            programName: '',
            cipCode: '',
            programDistanceEd: null,
            creditRequirementMinimum: '',
            creditRequirementMaximum: '',
        },
    ]);
    const [currentSplitIndex, setCurrentSplitIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const currentSplitProgram = splitPrograms[currentSplitIndex];

    const getDeliveryMode = deliveryMode => {
        if (deliveryMode === null || deliveryMode === undefined) return null;
        if (deliveryMode === 'On Campus & Online') {
            return 3;
        } else if (deliveryMode === 'On Campus Only') {
            return 1;
        } else {
            return 2;
        }
    };

    const setAllVars = async () => {
        setValidationError('');
        setUserInput(INITIAL_STATE);
        setSplitPrograms([
            {
                programName: INITIAL_STATE.programName,
                cipCode: INITIAL_STATE.cipCode,
                programDistanceEd: INITIAL_STATE.programDistanceEd,
                creditRequirementMinimum: INITIAL_STATE.creditRequirementMinimum,
                creditRequirementMaximum: INITIAL_STATE.creditRequirementMaximum,
            },
        ]);
        setCurrentSplitIndex(0);
        if (INITIAL_STATE.requestId && !isLoading) {
            setIsLoading(true);
            academicProgramsService
                .getSplitProgramRequests(INITIAL_STATE.requestId)
                .then(res => {
                    const newPrograms = [];
                    newPrograms.push({
                        requestId: INITIAL_STATE.requestId,
                        programName: INITIAL_STATE.programName,
                        cipCode: INITIAL_STATE.cipCode,
                        programDistanceEd: INITIAL_STATE.programDistanceEd,
                        creditRequirementMinimum: INITIAL_STATE.creditRequirementMinimum,
                        creditRequirementMaximum: INITIAL_STATE.creditRequirementMaximum,
                    });
                    res.forEach(program => {
                        newPrograms.push({
                            requestId: program.requestID,
                            programName: program.programName,
                            cipCode: {
                                label: program.cipCode + ' - ' + program.cipTitle,
                                value: program.cipCode,
                            },
                            programDistanceEd: getDeliveryMode(program.deliveryMode),
                            creditRequirementMinimum: program.creditRequirementMinimum,
                            creditRequirementMaximum: program.creditRequirementMaximum,
                        });
                    });
                    setSplitPrograms(newPrograms);
                });
            setIsLoading(false);
        }
    };
    useEffect(() => {
        setAllVars();
    }, [INITIAL_STATE]);

    const toggleModal = () => {
        toggle();
        setOnlineDropdownDisabled(false);
        setFiles(null);
        setUserInput(INITIAL_STATE);
        setCurrentSplitIndex(0);
        setValidationError('');
    };

    const [options, setOptions] = useReducer(reducer, {});
    const fetchOptions = useCallback(() => {
        academicProgramsService.getInstitutions().then(res => {
            setOptions({
                institutions: res?.map(x => {
                    return { value: x.label, label: x.value };
                }),
            });
        });
        academicProgramsService.getCips().then(res => {
            setOptions({
                cipCode: res[2],
            });
        });
    }, []);

    const onFileChange = useCallback(e => {
        setFiles(e.target.files);
    }, []);

    useEffect(() => {
        setOptions({ locations: [] });
        if (institutions.label !== undefined) {
            const query = queryString.stringify(
                { institutions: institutions.value },
                {
                    arrayFormat: 'separator',
                    skipEmptyString: true,
                }
            );
            academicProgramsService.getLocations(query).then(res => {
                setOptions({ locations: res?.locations });
            });
        } else if (userInput.institution) {
            const query = queryString.stringify(
                { institutions: userInput.institution.value },
                {
                    arrayFormat: 'separator',
                    skipEmptyString: true,
                }
            );
            academicProgramsService.getLocations(query).then(res => {
                setOptions({ locations: res.locations });
            });
        }
    }, [userInput.institution, institutions]);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    function hasValue(value) {
        return value !== null && value !== undefined && value !== '';
    }

    function hasText(value) {
        return hasValue(value) && String(value).trim() !== '';
    }

    function hasSelectValue(option) {
        return option && hasValue(option.value);
    }

    function getSplitProgramValidationError() {
        if (!hasSelectValue(userInput.degreeLevel)) {
            return { message: 'Degree/Award Level is required.' };
        }
        if (!hasSelectValue(userInput.programStatus)) {
            return { message: 'Program Status is required.' };
        }
        if (!splitPrograms.length) {
            return { message: 'At least one split program is required.' };
        }

        for (let i = 0; i < splitPrograms.length; i++) {
            const program = splitPrograms[i];
            if (!hasText(program.programName)) {
                return {
                    message: 'Program Name is required for program ' + (i + 1) + '.',
                    splitIndex: i,
                };
            }
            if (!hasSelectValue(program.cipCode)) {
                return {
                    message: 'CIP Code is required for program ' + (i + 1) + '.',
                    splitIndex: i,
                };
            }
            if (!hasValue(program.programDistanceEd)) {
                return {
                    message: 'Delivery Mode is required for program ' + (i + 1) + '.',
                    splitIndex: i,
                };
            }
        }

        return null;
    }

    const editProgramRequest = async () => {
        const splitValidationError = getSplitProgramValidationError();
        if (splitValidationError) {
            setValidationError(splitValidationError.message);
            if (hasValue(splitValidationError.splitIndex)) {
                setCurrentSplitIndex(splitValidationError.splitIndex);
            }
            return;
        }

        try {
            setValidationError('');
            const programsToSubmit = splitPrograms;
            let linkRequestId = -1;
            for (let index = 0; index < programsToSubmit.length; index++) {
                const program = programsToSubmit[index];
                const postData = {
                    requestId: program.requestId,
                    degreeLevel: userInput.degreeLevel
                        ? userInput.degreeLevel.value
                        : undefined,
                    formalName: program.programName ? program.programName : undefined,
                    cipCode: program.cipCode ? program.cipCode.value : undefined,
                    distanceEdApproved: {
                        id: program.programDistanceEd,
                    },
                    locationStatusChanges: getLocationStatusChanges([
                        ...locationsToAdd,
                        ...(userInput.locations || []),
                    ]),
                    comments: userInput.comments ? userInput.comments : undefined,
                    creditRequirementMinimum: program.creditRequirementMinimum
                        ? Number(program.creditRequirementMinimum)
                        : undefined,
                    creditRequirementMaximum: program.creditRequirementMaximum
                        ? Number(program.creditRequirementMaximum)
                        : undefined,
                    institutionId: institutions ? institutions.value : undefined,
                    credential: {
                        ProgramURL: userInput.programURL ? userInput.programURL : '',
                        Keywords: userInput.keywords ? userInput.keywords : '',
                        ProgramDescription: userInput.description
                            ? userInput.description
                            : '',
                        CredentialName: userInput.programName
                            ? userInput.programName
                            : undefined,
                    },
                    ProgramStatusId: userInput.programStatus.value,
                };
                if (index === 0) {
                    const res = await academicProgramsService
                        .editProgramRequest(postData)
                        .then(postRes => {
                            if (files) {
                                Promise.resolve(
                                    academicProgramsService.uploadFiles(
                                        files,
                                        postRes.programId
                                    )
                                );
                            }
                            linkRequestId = postRes.programId;
                            return postRes;
                        });
                } else {
                    const res = await academicProgramsService
                        .editProgramRequest(postData)
                        .then(postRes => {
                            return postRes;
                        });
                }
            }
            toggle();
            eventEmitter.emit('updateSARData')
            setUserInput(INITIAL_STATE);
        } catch (error) {
            setValidationError('Unable to submit the program split request.');
        }
    };

    const getLocationStatusChanges = locations => {
        return locations.map(location => {
            return {
                LocationId: location.value,
                LocationName: location.label,
                StatusId: location.statusId,
            };
        });
    };

    const closeBtn = (
        <button className="close" onClick={toggleModal}>
            &times;
        </button>
    );

    const rowStyles = {
        container: provided => ({
            ...provided,
            width: 120,
        }),
    };

    const statusChangeOptions = [
        { label: 'Active', value: 1 },
        { label: 'Suspended', value: 3 },
        { label: 'Eliminated', value: 4 },
    ];

    const getSelectOption = val => {
        return statusChangeOptions.find(x => x.value === val);
    };

    const handleSplitChange = e => {
        setValidationError('');
        const updatedSplits = [...splitPrograms];
        if (Object.prototype.hasOwnProperty.call(e, 'cipCode')) {
            updatedSplits[currentSplitIndex] = {
                ...updatedSplits[currentSplitIndex],
                ['cipCode']: e.cipCode,
            };
            setSplitPrograms(updatedSplits);
        } else if (Object.prototype.hasOwnProperty.call(e, 'programDistanceEd')) {
            updatedSplits[currentSplitIndex] = {
                ...updatedSplits[currentSplitIndex],
                ['programDistanceEd']: e.programDistanceEd,
            };
            setSplitPrograms(updatedSplits);
        } else {
            const { name, value } = e.target;
            updatedSplits[currentSplitIndex] = {
                ...updatedSplits[currentSplitIndex],
                [name]: value,
            };
            setSplitPrograms(updatedSplits);
        }
    };

    const handleNext = () => {
        if (currentSplitIndex < splitPrograms.length - 1) {
            setCurrentSplitIndex(currentSplitIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentSplitIndex > 0) {
            setCurrentSplitIndex(currentSplitIndex - 1);
        }
    };

    const handleAddProgramSplit = () => {
        setSplitPrograms([
            ...splitPrograms,
            {
                programName: '',
                cipCode: '',
                programDistanceEd: '',
                creditRequirementMinimum: '',
                creditRequirementMaximum: '',
            },
        ]);
        setCurrentSplitIndex(splitPrograms.length);
    };

    return (
        <Modal isOpen={isOpen} className="modal-dialog modal-lg">
            <ModalHeader close={closeBtn}>Edit Program Split Request</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <label>Institution</label>
                        <Input
                            type="text"
                            placeholder="Institution Name"
                            name="cipCode"
                            value={institutions.label}
                            disabled={true}
                        />
                    </FormGroup>
                    <>
                        <FormGroup>
                            <label>
                                {currentSplitIndex === 0 ? '' : 'New'} Program Name
                            </label>
                            <Input
                                type="text"
                                placeholder="Program Name"
                                onChange={handleSplitChange}
                                name="programName"
                                value={currentSplitProgram.programName}
                            />
                        </FormGroup>
                        <FormSelect
                            label={currentSplitIndex === 0 ? 'CIP Code' : 'New CIP Code'}
                            onChange={handleSplitChange}
                            options={
                                options.cipCode
                                    ? options.cipCode.map(x => {
                                        return {
                                            value: x.cipCode,
                                            label: `${x.cipCode} - ${x.cipName}`,
                                        };
                                    })
                                    : []
                            }
                            placeholder="CIP Code"
                            name="cipCode"
                            value={currentSplitProgram.cipCode}
                            isMulti={false}
                            getOptionLabel={getCipLabel}
                            isClearable
                            showLoading
                        />
                        <div>
                            <label className="d-block">
                                {currentSplitIndex === 0 ? '' : 'New'} Delivery Mode
                            </label>
                            <RadioCheckGroup
                                onChange={handleSplitChange}
                                name="programDistanceEd"
                                value={currentSplitProgram.programDistanceEd}
                                options={[
                                    {
                                        value: 1,
                                        label: 'On Campus Only',
                                    },
                                    {
                                        value: 2,
                                        label: 'Online Only',
                                    },
                                    {
                                        value: 3,
                                        label: 'On Campus & Online',
                                    },
                                ]}
                            />
                        </div>
                        <div>
                            <FormGroup>
                                <label>
                                    {currentSplitIndex === 0 ? '' : 'New'} Credit
                                    Requirement Minimum
                                </label>
                                <Input
                                    type="number"
                                    style={{ textAlign: 'left' }}
                                    placeholder="Minimum Credits"
                                    onChange={handleSplitChange}
                                    name="creditRequirementMinimum"
                                    value={currentSplitProgram.creditRequirementMinimum}
                                />
                            </FormGroup>
                        </div>

                        <div>
                            <FormGroup>
                                <label>
                                    {currentSplitIndex === 0 ? '' : 'New'} Credit
                                    Requirement Maximum
                                </label>
                                <Input
                                    type="number"
                                    style={{ textAlign: 'left' }}
                                    placeholder="Maximum Credits"
                                    onChange={handleSplitChange}
                                    name="creditRequirementMaximum"
                                    value={currentSplitProgram.creditRequirementMaximum}
                                />
                            </FormGroup>
                        </div>
                        <div className="d-flex justify-content-between mt-3">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary step-back"
                                onClick={() => handlePrevious()}
                                disabled={currentSplitIndex === 0}
                            >
                                Previous
                            </button>
                            <span>
                                Program {currentSplitIndex + 1} of {splitPrograms.length}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary step-next"
                                onClick={() => handleNext()}
                                disabled={currentSplitIndex === splitPrograms.length - 1}
                            >
                                Next
                            </button>
                        </div>
                    </>
                    <div className="form-row align-items-center">
                        <div className="col-lg-12">
                            <label>Support Documentation</label>
                        </div>
                    </div>
                    <div className="w-100 hidden-file-input-small">
                        <input
                            type="file"
                            accept=".csv,.zip,.pdf,.xls,.xlxs"
                            onChange={onFileChange}
                            multiple={true}
                        />
                        <button type="button" className="btn btn-outline-primary btn-sm">
                            <i className="far"></i> <FontAwesomeIcon icon={faUpload} />
                            Upload
                        </button>
                    </div>
                    {files &&
                        Array.from(files).map((x, index) => (
                            <h5 key={index} className="mb-0 mt-4 text-italic">
                                <FontAwesomeIcon icon={faPaperclip} /> {x.name}
                            </h5>
                        ))}
                    <FormGroup>
                        <label>
                            Please provide any additional information necessary to process
                            your request.
                        </label>
                        <Input
                            type="textarea"
                            rows="3"
                            placeholder="Place Comments Here"
                            onChange={evt =>
                                onChangeFunction.handleChange(evt, setUserInput)
                            }
                            name="comments"
                            value={userInput.comments}
                        />
                    </FormGroup>
                    {validationError && (
                        <div className="alert alert-danger" role="alert">
                            {validationError}
                        </div>
                    )}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggleModal}>
                    Cancel
                </Button>
                <Button color="primary" onClick={editProgramRequest}>
                    Submit
                </Button>{' '}
            </ModalFooter>
        </Modal>
    );
};
